import { noop } from 'lodash';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';

import { Middleware, ResponseContext } from '@neptune/shared/core-apis-middleware-domain';

import { ConnectivityServiceType } from './connectivity-service';

const RETRY_INTERVAL_MS = 2_000;
const TIMEOUT_MS = RETRY_INTERVAL_MS * 0.9;

export /* lib local */ class OnlineConnectionService implements ConnectivityServiceType {
  private state$ = new BehaviorSubject<'online' | 'reconnecting' | 'offline'>('online');
  private gatewayPromise?: Promise<void>;

  private pollingIntervalId?: ReturnType<typeof setInterval>;
  private connectionTimeoutId?: ReturnType<typeof setTimeout>;

  private connectionPromise?: Promise<boolean> = undefined;
  private abortConnection: () => void = noop;
  private resolve: () => void = noop;

  constructor(
    private checkUrl: string,
    private validateResponse: (data: unknown) => boolean,
  ) {
    window.addEventListener('online', () => this.goOnline());
    window.addEventListener('offline', () => this.goOffline());
  }

  get promise() {
    return this.gatewayPromise;
  }

  get asObservable() {
    return this.state$.pipe(distinctUntilChanged());
  }

  get isOnline() {
    return this.state$.value === 'online';
  }

  get isLocked() {
    return !this.isOnline;
  }

  goOffline() {
    if (this.state$.value === 'offline') {
      return;
    }

    this.clearPollingInterval();
    this.clearConnectionTimeout();
    this.abortConnection();
    this.state$.next('offline');
    this.createGatewayPromise();
  }

  goOnline() {
    void this.startConnectionAttempt({ silent: true });
  }

  async ensureConnection() {
    if (await this.checkConnection()) {
      return;
    }

    this.goOffline();
    throw new Error('Unable to establish connection to the server.');
  }

  private createGatewayPromise() {
    if (!this.gatewayPromise) {
      this.gatewayPromise = new Promise<void>((resolve) => {
        this.resolve = resolve;
      });
    }
  }

  private releaseGatewayPromise() {
    this.resolve();
    this.resolve = noop;
    this.gatewayPromise = undefined;
  }

  private clearConnectionTimeout() {
    if (this.connectionTimeoutId !== undefined) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = undefined;
    }
  }

  private clearPollingInterval() {
    if (this.pollingIntervalId !== undefined) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = undefined;
    }
  }

  private createPollingInterval() {
    if (this.pollingIntervalId === undefined) {
      this.pollingIntervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.startConnectionAttempt();
        }
      }, RETRY_INTERVAL_MS);
    }
  }

  private async checkConnection(timeout = TIMEOUT_MS) {
    if (this.connectionPromise != undefined) {
      return this.connectionPromise;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    this.abortConnection = () => {
      controller.abort();
      this.abortConnection = noop;

      this.clearConnectionTimeout();
    };

    this.connectionTimeoutId = setTimeout(() => {
      this.abortConnection();
    }, timeout);

    let isConnected = false;

    try {
      const response = await fetch(this.checkUrl, { signal });

      if (!response.ok) {
        isConnected = false;
      }

      const data = await response.json();

      isConnected = this.validateResponse(data);
    } catch {
      isConnected = false;
    } finally {
      this.connectionPromise = undefined;
      this.clearConnectionTimeout();
    }

    if (!isConnected) {
      this.createGatewayPromise();
    }

    return isConnected;
  }

  private async startConnectionAttempt({ silent = false } = {}) {
    if (!silent) {
      this.state$.next('reconnecting');
    }

    this.connectionPromise = this.checkConnection();

    this.createPollingInterval();

    if (await this.connectionPromise) {
      this.clearPollingInterval();

      this.state$.next('online');

      this.releaseGatewayPromise();
    }
  }

  /**
   * Middleware to ensure that the connection is established before making a request.
   * Note that there is a space to improve this middleware by checking if the connection is already
   * established when the request comes back in either successful or error response and retrying the
   * request. Customer's network setting may return even a valid response with a 200 status code and
   * an exotic content from the firewall or corporate proxy.
   */
  asOpenApiMiddleware(): Middleware {
    return {
      post: async (context: ResponseContext): Promise<Response | void> => {
        const status = context.response?.status ?? 200;

        if (status > 0 && status < 400) {
          return;
        }

        this.connectionPromise = this.checkConnection();

        if (await this.connectionPromise) {
          return;
        }

        void this.startConnectionAttempt();

        return;
      },
    };
  }
}

const validateApiResponse = (data: unknown) => {
  return typeof data === 'object' && data !== null && 'status' in data && data.status === 'ok';
};

export const onlineConnectionService = new OnlineConnectionService(
  '/api/status',
  validateApiResponse,
);
